/**
 * iter 72 (Phase C) — Branded "Migration in Progress" landing page.
 *
 * Mounted at /migration-in-progress. Users of an org with an active
 * (non-terminal) migration_job are routed here from the app shell so they
 * can't accidentally write data that won't survive the merge.
 *
 * Real-time updates: subscribes to the `migration_jobs` row via Supabase
 * Realtime so progress percentages move live. Falls back to 5s polling if
 * Realtime doesn't connect (Supabase's Realtime publication is per-project,
 * so the polling fallback is essential).
 *
 * When `status === 'completed'` we wipe local storage and force a fresh
 * sign-in (the user's session was killed server-side anyway).
 */
import { useEffect, useState } from "react";
import { ArrowsClockwise, CheckCircle, Hourglass, X, FileText } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { createClient } from "@supabase/supabase-js";

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
const sb = (SB_URL && SB_ANON) ? createClient(SB_URL, SB_ANON, { auth: { persistSession: false } }) : null;

const TERMINAL = new Set(["completed", "failed", "aborted"]);

const STATUS_LABELS = {
  pending_review: "Pending platform verification",
  approved:       "Approved — preparing to start",
  pre_flight:     "Pre-flight checks",
  countdown:      "Starting soon — save your work",
  in_progress:    "Migrating data",
  finalizing:     "Finalizing",
  paused:         "Paused — operator review needed",
  completed:      "Complete",
  failed:         "Failed",
  aborted:        "Aborted by operator",
};

export default function MigrationInProgressPage() {
  const { user, logout } = useAuth();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get(`/unify/jobs/active-for-source/${user?.org_id}`);
      setJob(r.data || null);
    } catch (e) {
      // 403 if user isn't a member, 404 if no active job.  Either way: bail.
      setJob(null);
    }
  };

  // Initial fetch + polling fallback (every 5s).
  useEffect(() => {
    if (!user?.org_id) return;
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [user?.org_id]);

  // Supabase Realtime subscription — primary live channel.  Listens for
  // UPDATEs on `migration_jobs` rows where source_org_id matches.  Each
  // batch-progress write fires this; we just re-pull.
  useEffect(() => {
    if (!sb || !user?.org_id) return;
    const channel = sb
      .channel("unify-progress")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "migration_jobs", filter: `source_org_id=eq.${user.org_id}` },
        () => refresh(),
      )
      .subscribe();
    return () => { try { sb.removeChannel(channel); } catch { /* */ } };
  }, [user?.org_id]);

  // If terminal — sign out the user (their session was killed server-side anyway).
  useEffect(() => {
    if (job && TERMINAL.has(job.status)) {
      // Brief delay so they SEE the success state before redirect.
      const t = setTimeout(() => { logout?.(); }, 3500);
      return () => clearTimeout(t);
    }
  }, [job?.status, logout]);

  const pct = job?.total_batches
    ? Math.min(100, Math.round((job.batches_completed / job.total_batches) * 100))
    : (job?.status === "completed" ? 100 : 0);

  const isComplete = job?.status === "completed";
  const isFailed = job?.status === "failed" || job?.status === "aborted";

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background" data-testid="migration-in-progress-page">
      <div className="surface max-w-2xl w-full p-8 space-y-6">
        <div className="flex items-center gap-3">
          {isComplete ? (
            <CheckCircle size={32} weight="duotone" className="text-emerald-500" />
          ) : isFailed ? (
            <X size={32} weight="duotone" className="text-destructive" />
          ) : (
            <Hourglass size={32} weight="duotone" className="text-primary animate-pulse" />
          )}
          <div>
            <div className="label-xs">ORGANIZATION UNIFICATION</div>
            <h1 className="heading text-2xl">
              {isComplete ? "Unification complete" : isFailed ? "Unification stopped" : "Unification in progress"}
            </h1>
          </div>
        </div>

        {!job ? (
          <div className="text-sm text-muted-foreground" data-testid="migration-no-active-job">
            We don&apos;t see an active migration for your organization. If you reached this
            page in error, <a href="/" className="underline">return to the app</a>.
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground" data-testid="migration-status-line">
              Your organization is being merged into another LogBook organization.
              All edits to this org&apos;s data are paused while we move it. You&apos;ll be
              automatically signed out and asked to sign in fresh once it&apos;s done.
            </p>

            {/* Progress bar */}
            <div data-testid="migration-progress-bar">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="label-xs">{STATUS_LABELS[job.status] || job.status}</span>
                <span className="mono">
                  {job.batches_completed}/{job.total_batches || "—"} batches
                  {pct > 0 ? ` · ${pct}%` : ""}
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                <div
                  className="h-2 bg-primary transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {job.current_table && (
                <div className="text-xs text-muted-foreground mt-2" data-testid="migration-current-table">
                  Currently moving: <code className="mono">{job.current_table}</code>
                </div>
              )}
            </div>

            {/* Errors */}
            {job.last_error && (
              <div className="surface p-3 border-destructive/40 text-xs" data-testid="migration-last-error">
                <div className="label-xs text-destructive mb-1">LAST ERROR</div>
                <pre className="whitespace-pre-wrap font-mono">{job.last_error}</pre>
              </div>
            )}

            {isComplete && (
              <div className="text-sm space-y-3" data-testid="migration-complete-msg">
                <div>Signing you out — please sign in to the unified organization in a moment.</div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    api.get(`/unify/jobs/${job.id}/receipt`, { responseType: "blob" })
                      .then(r => {
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `migration-receipt-${job.id.slice(0, 8)}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      })
                      .catch(() => { /* token may already be invalid */ });
                  }}
                  data-testid="migration-receipt-download-btn"
                >
                  <FileText size={14} weight="bold" /> DOWNLOAD MIGRATION RECEIPT (PDF)
                </button>
                <div className="text-xs text-muted-foreground">
                  Save this PDF — it&apos;s the legal/audit record of the merge.
                  Keep a copy with your acquisition paperwork.
                </div>
              </div>
            )}
            {isFailed && (
              <div className="text-sm text-destructive" data-testid="migration-failed-msg">
                The migration was stopped before completion. Your data is intact on this org.
                Contact LogBook support for next steps.
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <button
                className="btn btn-ghost btn-sm"
                onClick={refresh}
                data-testid="migration-refresh-btn"
                title="Force refresh"
              >
                <ArrowsClockwise size={12} /> REFRESH
              </button>
              <span className="text-[10px] text-muted-foreground">
                Live updates via Supabase Realtime · polling fallback every 5s.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
